/**
 * BMI question: compact single-screen slider redesign. Replaces the shared
 * BMISection's two bordered number-input rows (which read as two stacked
 * screens) with a single unified block: a labeled weight slider, a labeled
 * height slider (label sourced from question.text, same as the shared
 * BMISection), and the BMI result/eligibility message below. Answers are
 * always stored in cm/kg; when the answer's validation rule allows it
 * (allow_imperial_height / allow_imperial_weight, the UK), a toggle switches
 * the slider to ft/in or st/lb (imperial is the default there). Reuses
 * calculation/eligibility/threshold logic and the Significance component
 * from the shared BMISection rather than reimplementing them.
 *
 * Display defaults (min values) are visual only — answers stay empty until
 * the user moves a slider or types a value. On Continue without interaction, parent sets
 * questionnaireValidationObject[id] === false so we show errorMode
 * (red accent + shake + required message), matching SingleSelectAnswers /
 * NumberInput error feedback elsewhere in the questionnaire.
 */

import React, { useEffect, useMemo, useState } from "react";
import * as translator from "../../../utils/translator";
import { useShellId, useShellIdFn } from "./shellContext";
import Significance from "../components/Significance/Significance";
import UnitToggle from "../components/ImperialInput/UnitToggle";
import { IMPERIAL, IN_TO_CM, LB_TO_KG, imperialToStoredCm, imperialToStoredKg } from "../components/ImperialInput/imperialUtils";
import { formatFtIn, formatStLb, getUnitPref, saveUnitPref, UnitSystem } from "./units";
import {
  calculateBMI,
  extractBmiThresholds,
  getEligibilityStatus,
  computeHasComorbidity,
  getBmiStatusMessage,
  getSignificanceLevel,
} from "../components/BMISection/BMISection";
import { BMI_INPUT_LIMITS, BmiInputKind, checkBmiInput, usesBmiInputLimits } from "../components/BMISection/bmiLimits";
import DecimalInput from "../../../components/DecimalInput/DecimalInput";
import { normalizeDecimal } from "../../../utils/decimalInput";

interface MeasurementSliderProps {
  label?: string;
  value: number;
  min: number;
  max: number;
  unit: string;
  onChange: (value: number) => void;
  disabled?: boolean;
  errorMode?: boolean;
  errorMessage?: string;
  /** Changes on each failed Continue so the shake CSS animation restarts. */
  shakeKey?: number;
  /** DOM id for the range input itself, not the wrapper. */
  inputId?: string;
  /** Display text for a slider value; defaults to "{value} {unit}". */
  formatValue?: (value: number) => string;
  /** Rendered under the label row (the metric / imperial toggle). */
  unitToggle?: React.ReactNode;
}

const MeasurementSlider: React.FC<MeasurementSliderProps> = ({
  label,
  value,
  min,
  max,
  unit,
  onChange,
  disabled,
  errorMode,
  errorMessage,
  shakeKey = 0,
  inputId,
  formatValue = (v: number) => `${v} ${unit}`,
  unitToggle,
}) => {
  // Both shells render this slider, so scope the id to keep it unique.
  const shellInputId = useShellId(inputId);
  const clamped = Math.min(Math.max(value, min), max);
  const percent = max > min ? ((clamped - min) / (max - min)) * 100 : 0;
  const accent = errorMode ? "var(--primary-danger)" : "#f59402";
  const accentMid = errorMode ? "#f5a3a3" : "#efcc3f";
  const valueColor = errorMode ? "var(--primary-danger)" : "#f59402";

  return (
    <div
      key={errorMode ? `err-${shakeKey}` : "ok"}
      className={`w-full${errorMode ? " measurement-slider-error" : ""}`}
    >
      <div className="flex items-center justify-between mb-2">
        {label ? (
          <span
            className="font-['Poppins'] text-[15px] font-medium text-[#08080a]"
            dangerouslySetInnerHTML={{ __html: label }}
          />
        ) : (
          <span />
        )}
        <span
          className="font-['Poppins'] text-[15px] font-semibold"
          style={{ color: valueColor }}
        >
          {formatValue(clamped)}
        </span>
      </div>
      {unitToggle ? <div className="mb-3 flex justify-end">{unitToggle}</div> : null}
      {/* The visible track is its own element so the transparent range input
          on top can be larger than it: a 44px-tall input with a 44px thumb
          (drawn as the same 24px ring) gives a bigger tap/drag target, and
          the input is 20px wider than the track so the ring still stops
          flush with the track ends. */}
      <div className="relative h-6">
        <div
          aria-hidden
          className="absolute inset-x-0 top-1 h-4 rounded-full pointer-events-none"
          style={{
            background: `linear-gradient(to right, ${accent} 0%, ${accentMid} ${percent}%, #e5e7eb ${percent}%, #e5e7eb 100%)`,
          }}
        />
        <input
          id={shellInputId}
          type="range"
          min={min}
          max={max}
          value={clamped}
          disabled={disabled}
          onChange={(e) => onChange(Number(e.target.value))}
          className="measurement-slider"
          aria-invalid={errorMode || undefined}
          style={{
            // CSS variable read by the thumb ring rule below
            ["--slider-thumb-border" as string]: accent,
          }}
        />
      </div>
      <div className="flex items-center justify-between mt-1">
        <span className="font-['Poppins'] text-[13px] text-[#616975]">
          {formatValue(min)}
        </span>
        <span className="font-['Poppins'] text-[13px] text-[#616975]">
          {formatValue(max)}
        </span>
      </div>
      {errorMode && errorMessage ? (
        <p className="mt-2 font-['Poppins'] text-[13px] font-medium text-[var(--primary-danger)]">
          {errorMessage}
        </p>
      ) : null}
      <style>{`
        .measurement-slider {
          -webkit-appearance: none;
          appearance: none;
          position: absolute;
          top: -10px;
          left: -10px;
          width: calc(100% + 20px);
          height: 44px;
          margin: 0;
          padding: 0;
          background: transparent;
          outline: none;
        }
        /* 44px hit area, painted as the 24px white ring with a 4px border. */
        .measurement-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 44px;
          height: 44px;
          border-radius: 50%;
          border: none;
          background: radial-gradient(circle, #ffffff 7.5px, var(--slider-thumb-border, #f59402) 8px, var(--slider-thumb-border, #f59402) 11.5px, transparent 12px);
          cursor: pointer;
          transform: scale(1);
          transition: transform 0.15s ease;
        }
        /* Only :active — range inputs keep :focus after mouseup, which would leave the thumb large. */
        .measurement-slider:active::-webkit-slider-thumb {
          transform: scale(1.15);
        }
        .measurement-slider::-moz-range-thumb {
          width: 44px;
          height: 44px;
          border-radius: 50%;
          border: none;
          background: radial-gradient(circle, #ffffff 7.5px, var(--slider-thumb-border, #f59402) 8px, var(--slider-thumb-border, #f59402) 11.5px, transparent 12px);
          cursor: pointer;
          box-sizing: border-box;
          transform: scale(1);
          transition: transform 0.15s ease;
        }
        .measurement-slider:active::-moz-range-thumb {
          transform: scale(1.15);
        }
        .measurement-slider::-moz-range-track {
          height: 44px;
          background: transparent;
        }
        @keyframes measurement-slider-shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-6px); }
          40% { transform: translateX(6px); }
          60% { transform: translateX(-4px); }
          80% { transform: translateX(4px); }
        }
        .measurement-slider-error {
          animation: measurement-slider-shake 0.4s ease-in-out;
        }
      `}</style>
    </div>
  );
};

interface BMISliderSectionProps {
  question: any;
  formData: Record<string | number, any>;
  answersSelectedMap: Record<string | number, any>;
  questionnaireValidationObject?: Record<string | number, any>;
  /** Incremented by parent on each failed Continue to retrigger shake. */
  validationAttempt?: number;
  onTextChange: (
    answerId: number,
    text: string,
    validationRule: any,
    recipeQuestionId: number | null
  ) => void;
  handleRef?: any;
  onReveal?: (...args: any[]) => void;
  onHide?: (...args: any[]) => void;
  dangerSignificancesRefsDispatch: any;
  mention?: string;
  language?: string;
  flattenedQuestionnaire: any[];
  disabled?: boolean;
}

const DEFAULT_HEIGHT_MIN = 100;
const DEFAULT_HEIGHT_MAX = 220;
const DEFAULT_WEIGHT_MIN = 60;
const DEFAULT_WEIGHT_MAX = 200;

// The DB labels carry the metric unit ("Weight (kg)", "Height (cm)"); swap it for the unit on screen.
const withUnit = (label: string | undefined, unit: string) =>
  label?.replace(/\(\s*(kg|cm)\s*\)/i, `(${unit})`);

const BMISliderSection: React.FC<BMISliderSectionProps> = ({
  question,
  answersSelectedMap,
  questionnaireValidationObject = {},
  validationAttempt = 0,
  onTextChange,
  dangerSignificancesRefsDispatch,
  language,
  flattenedQuestionnaire,
  disabled = false,
}) => {
  const heightAnswersGroup = question?.answersGroup;
  const weightAnswerNode = question?.answersGroup?.children?.[0];

  const heightAnswerId = heightAnswersGroup?.recipe?.id;
  const weightAnswerId = weightAnswerNode?.recipe?.id;

  const heightRule = heightAnswersGroup?.recipe?.validation_rule;
  const weightRule = weightAnswerNode?.recipe?.validation_rule;

  const heightValue = Number(answersSelectedMap[heightAnswerId]) || 0;
  const weightValue = Number(answersSelectedMap[weightAnswerId]) || 0;

  const numericFields = usesBmiInputLimits(question);
  // Raw typed text, so a trailing "84," survives while the stored answer is "84.".
  const [heightText, setHeightText] = useState(() => String(answersSelectedMap[heightAnswerId] ?? "").replace(".", ","));
  const [weightText, setWeightText] = useState(() => String(answersSelectedMap[weightAnswerId] ?? "").replace(".", ","));
  const heightCheck = checkBmiInput(heightText, "height");
  const weightCheck = checkBmiInput(weightText, "weight");

  const heightMin = heightRule?.min_value ?? DEFAULT_HEIGHT_MIN;
  const heightMax = heightRule?.max_value ?? DEFAULT_HEIGHT_MAX;
  const weightMin = weightRule?.min_value ?? DEFAULT_WEIGHT_MIN;
  const weightMax = weightRule?.max_value ?? DEFAULT_WEIGHT_MAX;

  // Imperial slider steps are whole pounds / inches over the same metric range.
  const allowImperialWeight = !!weightRule?.allow_imperial_weight;
  const allowImperialHeight = !!heightRule?.allow_imperial_height;
  const [weightUnit, setWeightUnit] = useState<UnitSystem>(() =>
    allowImperialWeight ? getUnitPref("weight") ?? "imperial" : "metric"
  );
  const [heightUnit, setHeightUnit] = useState<UnitSystem>(() =>
    allowImperialHeight ? getUnitPref("height") ?? "imperial" : "metric"
  );
  const weightImperial = allowImperialWeight && weightUnit === "imperial";
  const heightImperial = allowImperialHeight && heightUnit === "imperial";
  // Recorded so the later screens (projection, health summary) use the unit on screen.
  useEffect(() => {
    if (allowImperialWeight) saveUnitPref("weight", weightUnit);
  }, [allowImperialWeight, weightUnit]);
  useEffect(() => {
    if (allowImperialHeight) saveUnitPref("height", heightUnit);
  }, [allowImperialHeight, heightUnit]);

  const weightLabel = weightAnswerNode?.recipe?.label || weightAnswerNode?.recipe?.text;
  const heightLabel = question?.text;

  // Unset answers (display defaults only) and out-of-range values both surface
  // as errorMode once Continue has stamped false into the validation object.
  const weightError = questionnaireValidationObject[weightAnswerId] === false;
  const heightError = questionnaireValidationObject[heightAnswerId] === false;
  const requiredMessage =
    translator.translate(language, "default", "QuestionnairePage/bmi_slider_required") || "";

  const { comorbidity: comorbidityThreshold, minimum: minimumThreshold } = extractBmiThresholds(question);

  const hasComorbidity = useMemo(
    () => computeHasComorbidity(question, flattenedQuestionnaire, answersSelectedMap),
    [flattenedQuestionnaire, answersSelectedMap, question]
  );

  const outOfHardRange = numericFields && (heightCheck === "hard" || weightCheck === "hard");
  const bmi = useMemo(
    () => (outOfHardRange ? null : calculateBMI(weightValue, heightValue)),
    [weightValue, heightValue, outOfHardRange]
  );

  const eligibility = useMemo(() => {
    if (bmi === null) return null;
    return getEligibilityStatus(bmi, hasComorbidity, comorbidityThreshold, minimumThreshold);
  }, [bmi, hasComorbidity, comorbidityThreshold, minimumThreshold]);

  const statusMessage = useMemo(
    () => getBmiStatusMessage(language, bmi, eligibility, hasComorbidity, comorbidityThreshold, minimumThreshold),
    [bmi, eligibility, hasComorbidity, language, comorbidityThreshold, minimumThreshold]
  );

  const significanceLevel = useMemo(() => getSignificanceLevel(eligibility), [eligibility]);
  const sectionId = useShellId("wl2_bmi_slider_section");
  const resultValueId = useShellId("wl2_bmi_result_value");

  const scopeId = useShellIdFn();
  const checkMessage = translator.translate(language, "default", "QuestionnairePage/bmi_check_value") || "";
  const renderDecimalField = (
    kind: BmiInputKind,
    answerId: number,
    text: string,
    setText: (value: string) => void,
    check: ReturnType<typeof checkBmiInput>,
    requiredError: boolean,
    recipeQuestionId: number | null
  ) => {
    const [min, max] = BMI_INPUT_LIMITS[kind].hard;
    return (
      <DecimalInput
        id={answerId ? scopeId(`answer_${answerId}`) : undefined}
        label={translator.translate(language, "default", `QuestionnairePage/health_summary_edit_${kind}_label`) || ""}
        unit={kind === "height" ? "cm" : "kg"}
        value={text}
        disabled={disabled}
        message={check !== "ok" ? checkMessage : requiredError ? requiredMessage : undefined}
        tone={check === "soft" ? "warning" : "error"}
        onChange={(value) => {
          setText(value);
          onTextChange(answerId, normalizeDecimal(value), { min_value: min, max_value: max }, recipeQuestionId);
        }}
      />
    );
  };

  const bmiResult = bmi !== null && (
    <div className="bmi-display-container">
      <div className="bmi-result">
        <span className="bmi-result-label">
          {translator.translate(language, "default", "QuestionnairePage/bmi_result_label")}:
        </span>
        <span id={resultValueId} className="bmi-result-value">{bmi.toLocaleString(language || "de", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}</span>
      </div>
      {statusMessage && significanceLevel && (
        // The shared Significance gap is too tight next to the BMI field.
        <div className="bmi-error-container [&_.significance-alert-icon]:!mr-3">
          <Significance
            level={significanceLevel}
            text={statusMessage}
            fontWeight="500"
            fontSize="14px"
            dangerSignificancesRefsDispatch={dangerSignificancesRefsDispatch}
            parentID={null}
            margin="10px 0"
          />
        </div>
      )}
    </div>
  );

  if (numericFields) {
    return (
      <div id={sectionId} className="flex flex-col gap-6">
        {renderDecimalField("height", heightAnswerId, heightText, setHeightText, heightCheck, heightError, heightAnswersGroup?.recipe?.recipe_question_id)}
        {renderDecimalField("weight", weightAnswerId, weightText, setWeightText, weightCheck, weightError, weightAnswerNode?.recipe?.recipe_question_id)}
        {bmiResult}
      </div>
    );
  }

  return (
    <div id={sectionId} className="flex flex-col gap-6 lg:gap-8">
      <MeasurementSlider
        inputId={weightAnswerId ? `answer_${weightAnswerId}` : undefined}
        label={weightImperial ? withUnit(weightLabel, IMPERIAL.labels.stLb) : weightLabel}
        value={weightImperial ? Math.round((weightValue || weightMin) / LB_TO_KG) : weightValue || weightMin}
        min={weightImperial ? Math.ceil(weightMin / LB_TO_KG) : weightMin}
        max={weightImperial ? Math.floor(weightMax / LB_TO_KG) : weightMax}
        unit="kg"
        formatValue={weightImperial ? (lb) => formatStLb(lb * LB_TO_KG) : undefined}
        unitToggle={
          allowImperialWeight ? (
            <UnitToggle
              isImperial={weightImperial}
              onToggle={(imperial) => setWeightUnit(imperial ? "imperial" : "metric")}
              metricLabel={IMPERIAL.labels.kg}
              imperialLabel={IMPERIAL.labels.stLb}
            />
          ) : undefined
        }
        disabled={disabled}
        errorMode={weightError}
        errorMessage={weightError ? requiredMessage : undefined}
        shakeKey={validationAttempt}
        onChange={(value) =>
          onTextChange(
            weightAnswerId,
            String(weightImperial ? imperialToStoredKg(0, value) : value),
            weightRule,
            weightAnswerNode?.recipe?.recipe_question_id
          )
        }
      />
      <MeasurementSlider
        inputId={heightAnswerId ? `answer_${heightAnswerId}` : undefined}
        label={heightImperial ? withUnit(heightLabel, IMPERIAL.labels.ftIn) : heightLabel}
        value={heightImperial ? Math.round((heightValue || heightMin) / IN_TO_CM) : heightValue || heightMin}
        min={heightImperial ? Math.ceil(heightMin / IN_TO_CM) : heightMin}
        max={heightImperial ? Math.floor(heightMax / IN_TO_CM) : heightMax}
        unit="cm"
        formatValue={heightImperial ? (inches) => formatFtIn(inches * IN_TO_CM) : undefined}
        unitToggle={
          allowImperialHeight ? (
            <UnitToggle
              isImperial={heightImperial}
              onToggle={(imperial) => setHeightUnit(imperial ? "imperial" : "metric")}
              metricLabel={IMPERIAL.labels.cm}
              imperialLabel={IMPERIAL.labels.ftIn}
            />
          ) : undefined
        }
        disabled={disabled}
        errorMode={heightError}
        errorMessage={heightError ? requiredMessage : undefined}
        shakeKey={validationAttempt}
        onChange={(value) =>
          onTextChange(
            heightAnswerId,
            String(heightImperial ? imperialToStoredCm(0, value) : value),
            heightRule,
            heightAnswersGroup?.recipe?.recipe_question_id
          )
        }
      />
      {bmiResult}
    </div>
  );
};

export default BMISliderSection;
